// src/providers/RouteChangeHandler.tsx
'use client';

import { useStore } from '@/providers/app-store-provider';
import { useAuth } from '@/providers/auth-provider';
import { ReportingCenter } from '@/reporting/reportingCenter';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { getRouteMetadata } from '@/utils/route-metadata';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { useRewriteRouterPrefetch } from '@/hooks/useRouterChange/useRewriteRouterPrefetch';

export default function RouteChangeHandler() {
  const pathname = usePathname();
  const { isAuthenticated } = useAuth();
  const setShowLoginRegisterDialog = useStore((state) => state.setShowLoginRegisterDialog);
  const setIsGlobalLoading = useStore((state) => state.setIsGlobalLoading);
  const isGloabalLoading = useStore((state) => state.isGlobalLoading);
  const inAppH5 = useStore((state) => state.inAppH5);
  const router = useRouter();
  const originalPush = useRef(router.push);

  useRewriteRouterPrefetch();

  useEffect(() => {
    // 控制Google评分徽章显示与隐藏
    handleGoogleRatingBadgeRender(pathname);
  }, [pathname]); // 仅关心路径可只依赖 pathname

  // 监听全局链接点击事件（包括 Next.js Link 组件）
  // TODO: 见鬼了，有些Link组件并不会触发router.push的事件，导致下面的router.push去做全局loading失效了，
  // 所以这里加这个a标签的劫持来满足这种异常条件！两个Loading我做了互斥
  // 按照目前的做法是Link会触发这个handleLinkClick事件，也会触发router.push的
  // 个别不会触发的走的是这个handleLinkClick事件来兜底
  // 剩余的一些按钮的点击直接调用router.push是直接走的router.push的劫持，不会走这个handleLinkClick事件
  useEffect(() => {
    const handleLinkClick = (e: MouseEvent) => {
      // 查找最近的 <a> 标签
      const anchor = (e.target as HTMLElement).closest('a');
      if (!anchor || inAppH5) return;

      const href = anchor.getAttribute('href');

      if (!href || href === '#') {
        // 判断 `href` 为空或为 '#'，通常无实际导航
        return;
      }

      // 判断是否绑定了 `onClick` 事件
      if (anchor.onclick || e.defaultPrevented) {
        // 如果点击被阻止，表示有自定义逻辑
        return;
      }

      // 判断是否为外部链接或特殊链接
      const isExternal = href.startsWith('http') || href.startsWith('//');
      const isAnchor = href.startsWith('#');
      const isMailto = href.startsWith('mailto:');
      const isTel = href.startsWith('tel:');
      const isDownload = anchor.hasAttribute('download');
      const isTargetBlank = anchor.getAttribute('target') === '_blank';

      // 如果是外部链接、锚点、下载、新窗口打开等，不显示 Loading
      if (isExternal || isAnchor || isMailto || isTel || isDownload || isTargetBlank) {
        return;
      }

      logger.info('RouteChangeHandler handleLinkClick isGloabalLoading', isGloabalLoading);

      if (isGloabalLoading) {
        return;
      }

      // 判断是否跳转到不同的路径
      try {
        const currentPath = window.location.pathname;
        const targetUrl = new URL(href, window.location.origin);
        const targetPath = targetUrl.pathname;

        // 只有跳转到不同页面时才显示 Loading
        if (targetPath !== currentPath) {
          setIsGlobalLoading(true);
        }
      } catch (error) {
        // URL 解析失败时忽略
        logger.error('RouteChangeHandler handleLinkClick URL parse error', error);
      }
    };

    // 使用捕获阶段来确保在 Next.js Link 之前捕获事件
    document.addEventListener('click', handleLinkClick, true);

    return () => {
      document.removeEventListener('click', handleLinkClick, true);
    };
  }, [setIsGlobalLoading, isGloabalLoading]);

  // 实现旧版的 plugins/auth.js 中逻辑 + 路由切换 Loading
  useEffect(() => {
    // 重写 push 方法
    router.push = async (href: string) => {
      try {
        // 1. 判断是否为锚点链接（页面内跳转）
        if (typeof href === 'string' && href.startsWith('#')) {
          // 锚点跳转不需要 Loading，直接执行
          return originalPush.current(href);
        }

        logger.info('RouteChangeHandler router.push', href, isGloabalLoading);

        // 2. 解析目标 URL
        const targetUrl = new URL(href, window.location.origin);
        const currentPath = window.location.pathname;
        const targetPath = targetUrl.pathname;

        // 3. 判断是否跳转到相同页面（pathname 相同）
        if (targetPath === currentPath) {
          // 只是 query 参数变化，不需要 Loading
          return originalPush.current(href);
        }

        // 4. 判断目标路由是否需要保护
        const metadata = getRouteMetadata(targetPath);
        const isProtected = metadata?.auth;

        // 5. 未登录且访问受保护路由 → 阻止跳转并显示登录弹窗
        if (isProtected && !isAuthenticated) {
          setShowLoginRegisterDialog(true); // 显示登录弹窗
          return; // 不执行原始跳转
        }

        // 6. 允许跳转 → 显示 Loading 并执行原始 push 方法
        // 排除在内嵌页面的loading操作，德语和法语后续下线，不需要整这么复杂了
        if (!isGloabalLoading && !inAppH5 && window.self === window.top) {
          setIsGlobalLoading(true);
        }
        return originalPush.current(href);
      } catch (error) {
        // URL 解析失败时，直接执行跳转
        logger.error('RouteChangeHandler router.push URL parse error', error);
        return originalPush.current(href);
      }
    };

    // 组件卸载时恢复原始方法
    return () => {
      router.push = originalPush.current;
    };
  }, [router, isAuthenticated, setShowLoginRegisterDialog, setIsGlobalLoading, isGloabalLoading]);

  return null;
}

/**
 * 处理谷歌评分徽章渲染
 * 配镜流程-购物车-订单payment页, 不展示。其他页面都展示。
 * @param pathname
 * @param search
 */
function handleGoogleRatingBadgeRender(pathname: string) {
  // 不展示的路由前缀
  const notShowRouter = ['/lens-process', '/frame-process', '/cart', '/payment'];
  // 仅当不在不展示名单中时才展示
  const shouldHide = notShowRouter.some((item) => pathname.startsWith(item));

  const googleRatingBadgeContainer = document.getElementById('___ratingbadge_0');
  // google-merchantwidget-iframe-wrapper
  const googleMerchantWidgetIframeWrapper = document.getElementById(
    'google-merchantwidget-iframe-wrapper',
  );

  const setTrigger = (show: boolean) => {
    if (googleRatingBadgeContainer) {
      googleRatingBadgeContainer.style.display = show ? 'block' : 'none';
    }
    if (googleMerchantWidgetIframeWrapper) {
      googleMerchantWidgetIframeWrapper.style.display = show ? 'block' : 'none';
    }
  };

  try {
    if (shouldHide) {
      logger.info('Route changed to:', '不需要展示 Google 徽章');
      setTrigger(false);
    } else {
      logger.info('Route changed to:', '需要展示 Google 徽章');
      setTrigger(true);
    }
  } catch (e) {
    logger.error('RouteChangeHandler handleGoogleRatingBadgeRender', e);
  }
}
